/**
 * useWallet Hook
 * Flutter source: wallet_screen.dart - state management + pagination logic
 *
 * Orchestrates wallet transaction fetching with infinite scroll pagination.
 * Reads wallet balance from Redux (app-level state).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { PageCursor } from '@/data/types/json';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { selectWallet } from '@/presentation/store/slices/wallet/walletSlice';
import { FetchWalletTransactionsUseCase } from '@/domain/usecases/wallet/fetchWalletTransactionsUseCase';
import { FetchAdminWalletTransactionsUseCase } from '@/domain/usecases/wallet/fetchAdminWalletTransactionsUseCase';
import { RefreshWalletTransactionsUseCase } from '@/domain/usecases/wallet/refreshWalletTransactionsUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseWalletResult {
  transactions: AppTransactionEntity[];
  wallet: ReturnType<typeof selectWallet>;
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useWallet = (isFromAdmin: boolean = false): UseWalletResult => {
  const { l10n } = useLocale();
  const wallet = useSelector(selectWallet);
  const [transactions, setTransactions] = useState<AppTransactionEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const lastFetchedItemRef = useRef<PageCursor | undefined>(undefined);
  // Captured in a ref so the stable `loadMore` (deps `[]`) reads the current
  // flag without being recreated. Mirrors `hasMoreRef`'s pattern.
  const isFromAdminRef = useRef(isFromAdmin);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
  }, [isLoading, hasMore]);

  useEffect(() => {
    isFromAdminRef.current = isFromAdmin;
  }, [isFromAdmin]);

  const loadMore = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const useCase = isFromAdminRef.current
        ? new FetchAdminWalletTransactionsUseCase()
        : new FetchWalletTransactionsUseCase();
      const result = await useCase.execute(lastFetchedItemRef.current);

      setTransactions(prev => {
        const existingIds = new Set(prev.map(t => `${t.usersInfo.userId}_${t.createdDate}`));
        const newTransactions = result.transactions.filter(
          t => !existingIds.has(`${t.usersInfo.userId}_${t.createdDate}`)
        );
        return [...prev, ...newTransactions];
      });

      setHasMore(result.hasMore);
      lastFetchedItemRef.current = result.cursor;
    } catch (err) {
      setError(l10n.common.genericError);
      appLogger.error('Error loading transactions', 'useWallet.loadMore', { error: err as Error });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (transactions.length === 0) {
      await loadMore();
      return;
    }

    setError(null);

    try {
      const latestTimestamp = transactions[0]?.updatedDate ?? transactions[0]?.createdDate ?? 0;
      const useCase = new RefreshWalletTransactionsUseCase();
      const result = await useCase.execute(latestTimestamp);

      if (result.transactions.length > 0) {
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => `${t.usersInfo.userId}_${t.createdDate}`));
          const newTransactions = result.transactions.filter(
            t => !existingIds.has(`${t.usersInfo.userId}_${t.createdDate}`)
          );
          return [...newTransactions, ...prev];
        });
      }
    } catch (err) {
      appLogger.error('Error refreshing transactions', 'useWallet.refresh', { error: err as Error });
    }
  }, [transactions, loadMore]);

  const initialize = useCallback(async () => {
    if (transactions.length === 0) {
      await loadMore();
    }
  }, [transactions.length, loadMore]);

  return {
    transactions,
    wallet,
    isLoading,
    hasMore,
    error,
    loadMore,
    refresh,
    initialize,
  };
};
